import { History, Location } from "history";
import { URL_PARAMS } from "~/pages/Billing/constants";

const isValidTabValue = <T extends string>(
  value: string,
  validValues: readonly T[],
): value is T => validValues.includes(value as T);

export const getTabFromUrl = <T extends string>(
  search: string,
  validValues: readonly T[],
  defaultValue: T,
): T => {
  const params = new URLSearchParams(search);
  const tabParam = params.get(URL_PARAMS.TAB_PARAM);
  if (tabParam && isValidTabValue(tabParam, validValues)) {
    return tabParam;
  }
  return defaultValue;
};

export const updateTabInUrl = (history: History, location: Location, tab: string): void => {
  const params = new URLSearchParams(location.search);
  params.set(URL_PARAMS.TAB_PARAM, tab);
  history.replace({ search: params.toString() });
};

export const serializeFilters = <T>(filters: T[] | null): string | undefined => {
  if (!filters || filters.length === 0) return undefined;
  try {
    const serialized = encodeURIComponent(JSON.stringify(filters));
    return serialized;
  } catch {
    return undefined;
  }
};

export const deserializeFilters = <T>(filtersParam: string | null): T[] | null => {
  if (!filtersParam) return null;
  try {
    const decoded = decodeURIComponent(filtersParam);
    const parsed = JSON.parse(decoded);
    const result = Array.isArray(parsed) ? parsed : null;
    return result;
  } catch {
    return null;
  }
};

export const serializeSorts = <T>(sorts: T[] | null): string | undefined => {
  if (!sorts || sorts.length === 0) return undefined;
  try {
    const serialized = encodeURIComponent(JSON.stringify(sorts));
    return serialized;
  } catch {
    return undefined;
  }
};

export const deserializeSorts = <T>(sortsParam: string | null): T[] | null => {
  if (!sortsParam) return null;
  try {
    const decoded = decodeURIComponent(sortsParam);
    const parsed = JSON.parse(decoded);
    const result = Array.isArray(parsed) ? parsed : null;
    return result;
  } catch {
    return null;
  }
};

export const parsePageParam = (pageParam: string | null): number => {
  if (!pageParam) return 0;
  try {
    const parsed = parseInt(pageParam, 10);
    const result = isNaN(parsed) || parsed <= 0 ? 0 : parsed - 1;
    return result;
  } catch {
    return 0;
  }
};

export const getUrlParams = (location: Location) => {
  try {
    const urlParams = new URLSearchParams(location.search);
    return {
      filters: urlParams.get(URL_PARAMS.FILTERS_PARAM),
      sorts: urlParams.get(URL_PARAMS.SORTS_PARAM),
      page: urlParams.get(URL_PARAMS.PAGE_PARAM),
    };
  } catch {
    return {
      filters: null,
      sorts: null,
      page: null,
    };
  }
};

export const updateUrlParams = <T, S>(
  history: History,
  location: Location,
  filters: T[] | null,
  sorts: S[] | null,
  currentPage: number,
  additionalParams?: Record<string, string>,
) => {
  // Preserve existing params (like 'tab') that are not managed by this function
  const params = new URLSearchParams(location.search);

  const filtersParam = serializeFilters(filters);
  if (filtersParam) {
    params.set(URL_PARAMS.FILTERS_PARAM, filtersParam);
  } else {
    params.delete(URL_PARAMS.FILTERS_PARAM);
  }

  const sortsParam = serializeSorts(sorts);
  if (sortsParam) {
    params.set(URL_PARAMS.SORTS_PARAM, sortsParam);
  } else {
    params.delete(URL_PARAMS.SORTS_PARAM);
  }

  if (currentPage >= 0) {
    const urlPageNumber = currentPage + 1;
    params.set(URL_PARAMS.PAGE_PARAM, urlPageNumber.toString());
  } else {
    params.delete(URL_PARAMS.PAGE_PARAM);
  }

  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });
  }

  const newSearch = params.toString();
  const currentSearch = location.search.replace(/^\?/, "");

  if (newSearch !== currentSearch) {
    history.replace({
      pathname: location.pathname,
      search: newSearch ? `?${newSearch}` : "",
    });
  }
};
